import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  canUseAppFromAccess,
  hasAccessDecision,
  legacySubscriptionAllowsApp,
  shouldShowAccessDeniedFromAccess,
  shouldShowPricingFromAccess,
} from "@/helpers";

interface OnboardingRouteProps {
  children: React.ReactNode;
}

function OnboardingRoute({ children }: OnboardingRouteProps) {
  const navigate = useNavigate();
  const { user, subscriptionStatus, accessDecision } = useAuth();

  useEffect(() => {
    // Check if user has already completed onboarding
    if (user?.user?.is_onboarding_completed) {
      navigate("/chat", { replace: true });
      return;
    }

    if (hasAccessDecision(accessDecision)) {
      const canUseApp = canUseAppFromAccess(accessDecision);
      const shouldShowPricing = shouldShowPricingFromAccess(accessDecision);
      const shouldShowAccessDenied =
        shouldShowAccessDeniedFromAccess(accessDecision);

      if (shouldShowPricing === true) {
        navigate("/pricing", { replace: true });
        return;
      }

      if (shouldShowAccessDenied === true || canUseApp === false) {
        navigate("/auth/access-denied", { replace: true });
        return;
      }

      if (canUseApp === true) {
        return;
      }
    }

    if (subscriptionStatus !== undefined && subscriptionStatus !== null) {
      if (!legacySubscriptionAllowsApp(subscriptionStatus)) {
        navigate("/pricing", { replace: true });
        return;
      }
    }
  }, [user, accessDecision, subscriptionStatus, navigate]);

  // Don't render children if onboarding is already completed
  if (user?.user?.is_onboarding_completed) {
    return null;
  }

  if (hasAccessDecision(accessDecision)) {
    const canUseApp = canUseAppFromAccess(accessDecision);
    const shouldShowPricing = shouldShowPricingFromAccess(accessDecision);
    const shouldShowAccessDenied =
      shouldShowAccessDeniedFromAccess(accessDecision);

    if (
      shouldShowPricing === true ||
      shouldShowAccessDenied === true ||
      canUseApp === false
    ) {
      return null;
    }

    if (canUseApp === true) {
      return <>{children}</>;
    }
  }

  if (subscriptionStatus !== undefined && subscriptionStatus !== null) {
    if (!legacySubscriptionAllowsApp(subscriptionStatus)) {
      return null;
    }
  }

  return <>{children}</>;
}

export default OnboardingRoute;
